// HubPage.js — Phase RT.2 unified Discovery hub
//
// The single canonical discovery surface for mchatai.com. Lists every
// user-published product with rating summary + whole-card tap targets.
// Replaces the prior split between /hub (curated Discover) and /play
// (raw triage grid) — one pool of user-generated products, no trust-
// tier distinctions, all reachable at /hub.
//
// Workflow:
//   1. User opens /hub
//   2. Browses + clicks any card → opens /hub/{slug} (details + play CTA)
//   3. In-game feedback widget pops a rating — rating posts to /v1/feedback
//   4. Back at /hub, star summary reflects new ratings on next load
//   5. RT.3 cull-script reads per-slug averages to soft-delete garbage
//
// Keyboard shortcuts:
//   ←/→    move focus between cards
//   Enter  open the focused product

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchEndProducts } from './communityApi';
import taxonomy from './data/taxonomy.json';
import { effectiveTopics } from './topicDeriver';
import { publishMillis, sortKeyTiebreak, byNewest } from './hubSort';
import { trackEvent } from './analytics';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://web-api-irnuiiphsq-uc.a.run.app';

// Keys are lowercased so they match productTypeKey() output regardless of how
// the source field was cased (contentCategory can be `miniApp`, `unityGame`, etc).
const CATEGORY_ICONS = {
  miniapp: '📱',
  game: '🎮',
  agent: '🤖',
  skill: '⚡',
  pipeline: '⛓️',
  macosapp: '🖥️',
  website: '🌐',
  microservice: '🖧',
  unitygame: '🕹️',
  podcast: '🎙️',
};

// Human-readable labels for the platform/type pill + filter dropdown.
// Keys are lowercased so they match productTypeKey() output regardless of how
// the source field was cased (contentCategory can be `miniApp`, `macOSApp`, etc).
const TYPE_LABELS = {
  miniapp: 'Web mini-app',
  macosapp: 'macOS app',
  game: 'Game',
  unitygame: 'Unity game',
  agent: 'Agent',
  skill: 'Skill',
  pipeline: 'Pipeline',
  microservice: 'Microservice',
  website: 'Website',
  podcast: 'Podcast',
};

function productTypeKey(product) {
  return (product.contentCategory || product.type || 'miniApp').toLowerCase();
}

function productCategoryEmoji(product) {
  return CATEGORY_ICONS[productTypeKey(product)] || CATEGORY_ICONS.miniapp;
}

// Mac apps are hidden from the web Hub for now (Lawrence, 2026-09-28): a
// visitor can't do much with one yet. The products stay published; this only
// keeps them out of the listing. Remove the entry to show them again.
const HIDDEN_CATEGORIES = new Set(['macosapp']);

// Shelves are the Hub's one row of filters. Every product sits on exactly one.
// A web app counts as a game when its curated tags put it under the taxonomy's
// Games pill (mchatai-source/frameworks/_taxonomy.json), the same rule iOS uses.
const SHELVES = [
  { id: 'games', label: 'Games' },
  { id: 'apps', label: 'Apps & pages' },
  { id: 'walks', label: 'Walks' },
  { id: 'images', label: 'Images' },
  { id: 'audio', label: 'Audio' },
];

function shelfOf(product) {
  const kind = productTypeKey(product);
  const sub = (product.subType || '').toLowerCase();
  if (kind === 'tour') return 'walks';
  if (kind === 'game' || kind === 'unitygame') return 'games';
  if (kind === 'miniapp') return effectiveTopics(product, taxonomy).includes('games') ? 'games' : 'apps';
  if (kind === 'prompt') return sub.startsWith('audio') ? 'audio' : 'images';
  if (kind === 'media') {
    const isAudio = product.audioPreviewURL || (product.productType || '').toLowerCase().includes('podcast');
    return isAudio ? 'audio' : 'images';
  }
  return 'apps';
}

function kindLabel(product) {
  const shelf = shelfOf(product);
  const sub = (product.subType || '').toLowerCase();
  if (shelf === 'games') return 'Game';
  if (shelf === 'walks') return 'Walk';
  if (shelf === 'apps') return (product.tags || []).includes('page') ? 'Page' : 'App';
  if (productTypeKey(product) === 'prompt') {
    if (sub === 'audio-library-speech') return 'Voice prompt';
    if (sub.startsWith('audio')) return 'Music prompt';
    if (sub === 'dreamsnap-video') return 'Video prompt';
    return 'Image prompt';
  }
  return shelf === 'audio' ? 'Podcast' : 'Image';
}

function TypePill({ product }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.2,
        color: '#374151',
        background: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: 999,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {kindLabel(product)}
    </span>
  );
}

// The shelf row: one pill per shelf, each with its count.
function ShelfRow({ options, value, onChange }) {
  return (
    <div role="tablist" aria-label="Show" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {options.map((opt) => {
        const sel = value === opt.id;
        return (
          <button key={opt.id}
            type="button"
            role="tab"
            aria-selected={sel}
            onClick={() => onChange(opt.id)}
            style={{
              padding: '6px 14px', fontSize: 13, fontWeight: 600,
              border: '1px solid ' + (sel ? '#111827' : '#e5e7eb'),
              background: sel ? '#111827' : '#fff',
              color: sel ? '#fff' : '#374151',
              borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'background 0.12s, border-color 0.12s',
            }}>
            {opt.label}
            {typeof opt.count === 'number' && (
              <span style={{ marginLeft: 6, fontWeight: 500, opacity: sel ? 0.75 : 0.55 }}>{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ProductIcon({ product, size = 44 }) {
  const src =
    (product.iconBase64 && (product.iconBase64.startsWith('data:')
      ? product.iconBase64
      : `data:image/png;base64,${product.iconBase64}`))
    || product.iconURL
    || null;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return (
    <div style={{ fontSize: size * 0.7, lineHeight: 1, width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {productCategoryEmoji(product)}
    </div>
  );
}

function detailURL(product) {
  const slug = product.slug || product.packageId || product._id;
  return `/hub/${encodeURIComponent(slug)}`;
}

function StarSummary({ avg, count }) {
  const filled = Math.round(avg || 0);
  const hasRating = count > 0;
  return (
    <div
      title={hasRating ? `${(avg ?? 0).toFixed(2)} avg (${count} rating${count === 1 ? '' : 's'})` : 'No ratings yet'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 12,
        userSelect: 'none',
        color: hasRating ? '#ffc940' : '#d1d5db',
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ color: i <= filled ? '#ffc940' : '#e5e7eb' }}>★</span>
      ))}
      {hasRating && (
        <span style={{ color: '#6b7280', marginLeft: 3, fontSize: 10 }}>
          {avg?.toFixed(1)}
        </span>
      )}
    </div>
  );
}

function ProductCard({ product, ratingSummary, isFocused, onFocus }) {
  const slug = product.slug || product.packageId || product._id;
  const target = detailURL(product);

  // Shared card inner content so we don't duplicate between <a> and <Link>.
  const cardBody = (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <ProductIcon product={product} size={44} />
        <StarSummary avg={ratingSummary?.avg} count={ratingSummary?.count || 0} />
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: '#111827' }}>
          {product.name || slug}
        </div>
        <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <TypePill product={product} />
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4, minHeight: 32 }}>
          {(product.summary || product.description || '').slice(0, 120)}
        </div>
      </div>
    </>
  );

  const sharedCardStyle = {
    background: '#fff',
    border: isFocused ? '2px solid #3b82f6' : '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxSizing: 'border-box',
    transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.1s',
    boxShadow: isFocused ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
    outline: 'none',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
  };

  const hoverIn = (e) => {
    if (!isFocused) e.currentTarget.style.borderColor = '#3b82f6';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const hoverOut = (e) => {
    if (!isFocused) e.currentTarget.style.borderColor = '#e5e7eb';
    e.currentTarget.style.transform = 'translateY(0)';
  };

  // Whole-card tap target opens the canonical Hub detail page. The raw
  // /u/{uid}/{slug}/ URL is only the hosted app iframe source, so linking
  // directly to it skips metadata, screenshots, ratings, and remix context.
  return (
    <Link
      to={target}
      tabIndex={0}
      onFocus={onFocus}
      onMouseEnter={(e) => { onFocus(); hoverIn(e); }}
      onMouseLeave={hoverOut}
      style={sharedCardStyle}
    >
      {cardBody}
    </Link>
  );
}

async function fetchBulkSummaries(slugs) {
  if (!slugs.length) return {};
  const chunks = [];
  for (let i = 0; i < slugs.length; i += 50) {
    chunks.push(slugs.slice(i, i + 50));
  }
  const out = {};
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const resp = await fetch(API_BASE + '/v1/ratings/summary-bulk?slugs=' + encodeURIComponent(chunk.join(',')));
        const json = await resp.json();
        if (json.ok && json.summaries) Object.assign(out, json.summaries);
      } catch (err) {
        console.warn('[Play] ratings summary-bulk failed:', err.message);
      }
    })
  );
  return out;
}

export default function HubPage() {
  // Phase HUB-REDESIGN-3 (2026-05-24) — filter state is URL-synced. The
  // detail page's clickable category/subType/model chips link to
  // /hub?category=X&subType=Y&model=Z; we read those params into local state
  // on first render and write changes back so deep-linked filters work and
  // the browser back button reverts cleanly. `replace: true` on the back-
  // write avoids stacking a history entry per keystroke in the search box.
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'hot');
  const [shelf, setShelf] = useState(() => searchParams.get('shelf') || '');
  // category / topic / subType / model are no longer on screen as pill rows.
  // They stay readable from the URL so the detail page's chips and old links
  // (/hub?category=prompt&subType=dreamsnap) still filter; a single chip
  // shows the active one and clears it.
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('category') || '');
  // Phase TOPIC (2026-06-21) — the orthogonal "topic" axis (Games/3D/Music/...).
  // Single-select, URL-synced via ?topic=. Pills + filtering come from the
  // bundled taxonomy mirror (mchatai-source/_taxonomy.json), per RULE #1.
  const [typeTopic, setTypeTopic] = useState(() => searchParams.get('topic') || '');
  const [promptApplet, setPromptApplet] = useState(() => searchParams.get('subType') || '');
  const [modelFilter, setModelFilter] = useState(() => searchParams.get('model') || '');
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [focusIndex, setFocusIndex] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const gridRef = useRef(null);

  // Push state → URL whenever filters change. `replace: true` so we don't
  // stack a history entry per character of search input. Empty values drop
  // their param so /hub stays the canonical URL when no filter is active.
  useEffect(() => {
    const next = new URLSearchParams();
    if (shelf) next.set('shelf', shelf);
    if (typeFilter) next.set('category', typeFilter);
    if (typeTopic) next.set('topic', typeTopic);
    if (typeFilter === 'prompt' && promptApplet) next.set('subType', promptApplet);
    if (modelFilter) next.set('model', modelFilter);
    if (search.trim()) next.set('q', search.trim());
    if (sortBy && sortBy !== 'hot') next.set('sort', sortBy);
    setSearchParams(next, { replace: true });
  }, [shelf, typeFilter, typeTopic, promptApplet, modelFilter, search, sortBy, setSearchParams]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rawList = await fetchEndProducts({});
        if (cancelled) return;
        const list = rawList.filter((p) => !HIDDEN_CATEGORIES.has(productTypeKey(p)));
        setProducts(list);
        const slugs = list.map((p) => p.slug || p.packageId || p._id).filter(Boolean);
        const s = await fetchBulkSummaries(slugs);
        if (!cancelled) setSummaries(s);
      } catch (err) {
        console.error('[Play] load failed:', err);
        if (!cancelled) setStatusMsg('Failed to load products.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter + sort
  const visible = useMemo(() => {
    let out = [...products];
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((p) =>
        [p.name, p.summary, p.description, p.publisherName, ...(p.tags || [])]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    if (typeFilter) {
      // Phase HUB-REDESIGN-3 (2026-05-24) — "podcast" is a productType
      // stamped by the macOS publish flow, not a contentCategory (those
      // items still live as contentCategory="media"). Match against
      // either field so the podcast badge's /hub?category=podcast link
      // collects the right corpus. Other filters keep the
      // contentCategory-only semantics.
      const tf = typeFilter.toLowerCase();
      out = out.filter((p) => {
        const cc = (p.contentCategory || p.type || '').toLowerCase();
        const pt = (p.productType || '').toLowerCase();
        if (tf === 'podcast') return pt.includes('podcast');
        return cc === tf;
      });
    }
    // PC.13 — applet sub-filter when typeFilter === 'prompt'.
    if (typeFilter === 'prompt' && promptApplet) {
      out = out.filter((p) => (p.subType || '') === promptApplet);
    }
    // Phase HUB-REDESIGN-3 — model filter, sourced from the detail-page
    // model chip's `/hub?model=...` link. Matches against generatorName
    // (mini-apps / macOS apps) OR modelHint (prompts), since those are the
    // two fields the chip pulls from. Case-insensitive substring match so
    // "openai" matches "OpenAI gpt-4o-mini" and "Lyria" matches
    // "google/lyria-3-pro-preview".
    if (modelFilter) {
      const m = modelFilter.toLowerCase();
      out = out.filter((p) => {
        const a = (p.generatorName || '').toLowerCase();
        const b = (p.modelHint || '').toLowerCase();
        return a.includes(m) || b.includes(m);
      });
    }
    // Phase TOPIC (2026-06-21) — orthogonal topic-axis filter. Single-select:
    // keep items whose current metadata-derived effective topics include the
    // selected pill.
    if (typeTopic) {
      out = out.filter((p) => effectiveTopics(p, taxonomy).includes(typeTopic));
    }
    if (shelf) {
      out = out.filter((p) => shelfOf(p) === shelf);
    }
    switch (sortBy) {
      case 'hot': {
        // Bayesian-weighted score (prior mean 3.5, weight 5). A single 5★
        // can't catapult an item to the top because the prior drags it back
        // toward average; only items with consistent ratings AND volume rise.
        const PRIOR_AVG = 3.5;
        const PRIOR_W = 5;
        const score = (p) => {
          const s = summaries[p.slug || p.packageId || p._id] || {};
          const n = s.count || 0;
          const avg = s.avg || 0;
          return (avg * n + PRIOR_AVG * PRIOR_W) / (n + PRIOR_W);
        };
        out.sort((a, b) => {
          const da = score(b) - score(a);
          if (da !== 0) return da;
          // tie: newer first (robust to Timestamp/string/number shapes)
          const dt = publishMillis(b) - publishMillis(a);
          if (dt !== 0) return dt;
          return sortKeyTiebreak(a).localeCompare(sortKeyTiebreak(b));
        });
        break;
      }
      case 'top':
        out.sort((a, b) => {
          const sa = summaries[a.slug || a.packageId || a._id] || {};
          const sb = summaries[b.slug || b.packageId || b._id] || {};
          const avgA = sa.count >= 1 ? sa.avg || 0 : -1;
          const avgB = sb.count >= 1 ? sb.avg || 0 : -1;
          if (avgB !== avgA) return avgB - avgA;
          return (sb.count || 0) - (sa.count || 0);
        });
        break;
      case 'most-feedback':
        out.sort((a, b) => {
          const sa = summaries[a.slug || a.packageId || a._id] || {};
          const sb = summaries[b.slug || b.packageId || b._id] || {};
          return (sb.count || 0) - (sa.count || 0);
        });
        break;
      case 'unjudged':
        out = out.filter((p) => {
          const s = summaries[p.slug || p.packageId || p._id];
          return !s || s.count === 0;
        });
        break;
      case 'newest':
      default:
        // Robust to ISO-string, Firestore Timestamp, and epoch-number shapes
        // (raw new Date(timestampObj) -> NaN was the "newest reads alphabetical"
        // bug). Equal timestamps fall to a stable slug order. See hubSort.js.
        out.sort(byNewest);
    }
    return out;
  }, [products, summaries, search, shelf, typeFilter, typeTopic, promptApplet, modelFilter, sortBy]);

  // Fire a debounced `search` event (~600ms after typing stops) so we learn
  // what people look for — including misses — without one event per keystroke.
  // search_term here is a hub query (game/app names), not personal data.
  useEffect(() => {
    const term = search.trim();
    if (!term) return undefined;
    const t = setTimeout(() => {
      trackEvent('search', { search_term: term.slice(0, 100), result_count: visible.length });
    }, 600);
    return () => clearTimeout(t);
  }, [search, visible.length]);

  // Phase RT.2 v2: arrow-key navigation only. Rating lives on the product page
  // (whole-card click opens it), so the /play grid only needs nav shortcuts.
  useEffect(() => {
    const handler = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      if (!visible.length) return;
      if (e.key === 'ArrowRight') {
        setFocusIndex((i) => Math.min(visible.length - 1, i + 1));
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        setFocusIndex((i) => Math.max(0, i - 1));
        e.preventDefault();
      } else if (e.key === 'Enter') {
        const product = visible[focusIndex];
        if (product) window.location.href = detailURL(product);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, focusIndex]);

  // Keep focusIndex in bounds when filters change.
  useEffect(() => {
    if (focusIndex >= visible.length) setFocusIndex(Math.max(0, visible.length - 1));
  }, [visible.length, focusIndex]);

  // Shelf pills with their counts. Empty shelves are left out.
  const shelfOptions = useMemo(() => {
    const counts = {};
    products.forEach((p) => { const s = shelfOf(p); counts[s] = (counts[s] || 0) + 1; });
    return [
      { id: '', label: 'All', count: products.length },
      ...SHELVES.filter((s) => counts[s.id] || s.id === shelf).map((s) => ({ ...s, count: counts[s.id] || 0 })),
    ];
  }, [products, shelf]);

  // One chip stands in for the old Type / Topic / Applet / Model rows when a
  // link arrives with one of those filters set.
  const legacyFilterLabel = useMemo(() => {
    const parts = [];
    if (typeFilter) parts.push(TYPE_LABELS[typeFilter.toLowerCase()] || typeFilter);
    if (promptApplet) parts.push(promptApplet);
    if (typeTopic) parts.push((taxonomy.pills || []).find((p) => p.id === typeTopic)?.label || typeTopic);
    if (modelFilter) parts.push(modelFilter);
    return parts.join(' · ');
  }, [typeFilter, promptApplet, typeTopic, modelFilter]);
  const clearLegacyFilters = () => { setTypeFilter(''); setPromptApplet(''); setTypeTopic(''); setModelFilter(''); };

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <header style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Hub</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
            Games, apps, walks, images and audio made with mChatAI. Open one to try it; its ⓘ button has the details.
          </p>
        </div>
        {/* Leaderboards moved here from top nav (mobile-first nav consolidation). */}
        <Link to="/leaderboards" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 999,
          background: '#fff', border: '1px solid #e5e7eb',
          color: '#0f172a', textDecoration: 'none',
          fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4z"/><path d="M17 4h3v3a3 3 0 0 1-3 3"/><path d="M7 4H4v3a3 3 0 0 0 3 3"/>
          </svg>
          Leaderboards
        </Link>
      </header>

      {/* Toolbar — search, sort, and one row of shelves (2026-09-28: replaced
          the Sort / Type / Topic pill rows, which buried a new visitor). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            type="search"
            placeholder="Search the Hub"
            aria-label="Search the Hub"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 220px',
              minWidth: 0,
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              outline: 'none',
              background: '#fff',
            }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280' }}>
            Sort
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ padding: '7px 8px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', color: '#111827' }}
            >
              <option value="hot">Popular</option>
              <option value="newest">Newest</option>
              {/* Older links can carry the rating sorts; keep them selectable when they do. */}
              {sortBy === 'top' && <option value="top">Top rated</option>}
              {sortBy === 'most-feedback' && <option value="most-feedback">Most rated</option>}
              {sortBy === 'unjudged' && <option value="unjudged">Unrated only</option>}
            </select>
          </label>
        </div>
        <ShelfRow options={shelfOptions} value={shelf} onChange={setShelf} />
        {legacyFilterLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Filtered by</span>
            <button
              type="button"
              onClick={clearLegacyFilters}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              title="Clear this filter"
            >
              {legacyFilterLabel}
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>×</span>
            </button>
          </div>
        )}
      </div>

      {/* Status message */}
      {statusMsg && (
        <div style={{ padding: '8px 12px', marginBottom: 12, background: '#eff6ff', color: '#1e40af', borderRadius: 6, fontSize: 13 }}>
          {statusMsg}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading products…</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No products match your filters.</div>
      ) : (
        <div
          ref={gridRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          {visible.map((p, idx) => {
            const slug = p.slug || p.packageId || p._id;
            return (
              <ProductCard
                key={p._id || slug || idx}
                product={p}
                ratingSummary={summaries[slug]}
                isFocused={idx === focusIndex}
                onFocus={() => setFocusIndex(idx)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
